import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query'
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query'
import axios from 'axios'
import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios'
import type {
  DeleteUser401,
  DeleteUser403,
  DeleteUser404,
  DeleteUser500,
  GetAllUsers200,
  GetAllUsers401,
  GetAllUsers500,
  GetAllUsersParams,
  GetUserById200,
  GetUserById401,
  GetUserById404,
  GetUserById500,
  HealthCheck200,
  UpdateUser200,
  UpdateUser401,
  UpdateUser403,
  UpdateUser404,
  UpdateUser500,
  UpdateUserBody
} from './types'


type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;



/**
 * @summary 모든 사용자 목록 조회
 */
export const getAllUsers = (
    params?: GetAllUsersParams, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetAllUsers200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/users`,{
    ...options,
        params: {...params, ...options?.params},}
    );
  }


export const getGetAllUsersQueryKey = (params?: GetAllUsersParams,) => {
    return [`https://api.ugot.uk/api/users`, ...(params ? [params]: [])] as const;
    }

    
export const getGetAllUsersInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getAllUsers>>, TError = AxiosError<GetAllUsers401 | GetAllUsers500>>(params?: GetAllUsersParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getAllUsers>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAllUsersQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllUsers>>> = ({ signal }) => getAllUsers(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getAllUsers>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAllUsersInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getAllUsers>>>
export type GetAllUsersInfiniteQueryError = AxiosError<GetAllUsers401 | GetAllUsers500>

/**
 * @summary 모든 사용자 목록 조회
 */
export const useGetAllUsersInfinite = <TData = Awaited<ReturnType<typeof getAllUsers>>, TError = AxiosError<GetAllUsers401 | GetAllUsers500>>(
 params?: GetAllUsersParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getAllUsers>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetAllUsersInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetAllUsersQueryOptions = <TData = Awaited<ReturnType<typeof getAllUsers>>, TError = AxiosError<GetAllUsers401 | GetAllUsers500>>(params?: GetAllUsersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAllUsers>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAllUsersQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllUsers>>> = ({ signal }) => getAllUsers(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAllUsers>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAllUsersQueryResult = NonNullable<Awaited<ReturnType<typeof getAllUsers>>>
export type GetAllUsersQueryError = AxiosError<GetAllUsers401 | GetAllUsers500>

/**
 * @summary 모든 사용자 목록 조회
 */
export const useGetAllUsers = <TData = Awaited<ReturnType<typeof getAllUsers>>, TError = AxiosError<GetAllUsers401 | GetAllUsers500>>(
 params?: GetAllUsersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAllUsers>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetAllUsersQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * @summary 특정 사용자 정보 조회
 */
export const getUserById = (
    userId: string, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetUserById200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/users/${userId}`,options
    );
  }


export const getGetUserByIdQueryKey = (userId: string,) => {
    return [`https://api.ugot.uk/api/users/${userId}`] as const;
    }

    
export const getGetUserByIdInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getUserById>>, TError = AxiosError<GetUserById401 | GetUserById404 | GetUserById500>>(userId: string, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getUserById>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetUserByIdQueryKey(userId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserById>>> = ({ signal }) => getUserById(userId, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(userId),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getUserById>>, TError, TData> & { queryKey: QueryKey }
}

export type GetUserByIdInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getUserById>>>
export type GetUserByIdInfiniteQueryError = AxiosError<GetUserById401 | GetUserById404 | GetUserById500>

/**
 * @summary 특정 사용자 정보 조회
 */
export const useGetUserByIdInfinite = <TData = Awaited<ReturnType<typeof getUserById>>, TError = AxiosError<GetUserById401 | GetUserById404 | GetUserById500>>(
 userId: string, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getUserById>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetUserByIdInfiniteQueryOptions(userId,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetUserByIdQueryOptions = <TData = Awaited<ReturnType<typeof getUserById>>, TError = AxiosError<GetUserById401 | GetUserById404 | GetUserById500>>(userId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getUserById>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetUserByIdQueryKey(userId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserById>>> = ({ signal }) => getUserById(userId, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(userId),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getUserById>>, TError, TData> & { queryKey: QueryKey }
}

export type GetUserByIdQueryResult = NonNullable<Awaited<ReturnType<typeof getUserById>>>
export type GetUserByIdQueryError = AxiosError<GetUserById401 | GetUserById404 | GetUserById500>

/**
 * @summary 특정 사용자 정보 조회
 */
export const useGetUserById = <TData = Awaited<ReturnType<typeof getUserById>>, TError = AxiosError<GetUserById401 | GetUserById404 | GetUserById500>>(
 userId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getUserById>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetUserByIdQueryOptions(userId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * @summary 사용자 정보 업데이트
 */
export const updateUser = (
    userId: string,
    updateUserBody: UpdateUserBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<UpdateUser200>> => {
    
    return axios.put(
      `https://api.ugot.uk/api/users/${userId}`,
      updateUserBody,options
    );
  }



export const getUpdateUserMutationOptions = <TError = AxiosError<UpdateUser401 | UpdateUser403 | UpdateUser404 | UpdateUser500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateUser>>, TError,{userId: string;data: UpdateUserBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof updateUser>>, TError,{userId: string;data: UpdateUserBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateUser>>, {userId: string;data: UpdateUserBody}> = (props) => {
          const {userId,data} = props ?? {};

          return  updateUser(userId,data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateUserMutationResult = NonNullable<Awaited<ReturnType<typeof updateUser>>>
    export type UpdateUserMutationBody = UpdateUserBody
    export type UpdateUserMutationError = AxiosError<UpdateUser401 | UpdateUser403 | UpdateUser404 | UpdateUser500>

    /**
 * @summary 사용자 정보 업데이트
 */
export const useUpdateUser = <TError = AxiosError<UpdateUser401 | UpdateUser403 | UpdateUser404 | UpdateUser500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateUser>>, TError,{userId: string;data: UpdateUserBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof updateUser>>,
        TError,
        {userId: string;data: UpdateUserBody},
        TContext
      > => {

      const mutationOptions = getUpdateUserMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * @summary 사용자 삭제
 */
export const deleteUser = (
    userId: string, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<void>> => {
    
    return axios.delete(
      `https://api.ugot.uk/api/users/${userId}`,options
    );
  }



export const getDeleteUserMutationOptions = <TError = AxiosError<DeleteUser401 | DeleteUser403 | DeleteUser404 | DeleteUser500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteUser>>, TError,{userId: string}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof deleteUser>>, TError,{userId: string}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteUser>>, {userId: string}> = (props) => {
          const {userId} = props ?? {};

          return  deleteUser(userId,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteUserMutationResult = NonNullable<Awaited<ReturnType<typeof deleteUser>>>
    
    export type DeleteUserMutationError = AxiosError<DeleteUser401 | DeleteUser403 | DeleteUser404 | DeleteUser500>

    /**
 * @summary 사용자 삭제
 */
export const useDeleteUser = <TError = AxiosError<DeleteUser401 | DeleteUser403 | DeleteUser404 | DeleteUser500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteUser>>, TError,{userId: string}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof deleteUser>>,
        TError,
        {userId: string},
        TContext
      > => {

      const mutationOptions = getDeleteUserMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * @summary 서비스 상태 확인
 */
export const healthCheck = (
     options?: AxiosRequestConfig
 ): Promise<AxiosResponse<HealthCheck200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/users/health`,options
    );
  }


export const getHealthCheckQueryKey = () => {
    return [`https://api.ugot.uk/api/users/health`] as const;
    }

    
export const getHealthCheckInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof healthCheck>>, TError = AxiosError<unknown>>( options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof healthCheck>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getHealthCheckQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof healthCheck>>> = ({ signal }) => healthCheck({ signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof healthCheck>>, TError, TData> & { queryKey: QueryKey }
}

export type HealthCheckInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof healthCheck>>>
export type HealthCheckInfiniteQueryError = AxiosError<unknown>

/**
 * @summary 서비스 상태 확인
 */
export const useHealthCheckInfinite = <TData = Awaited<ReturnType<typeof healthCheck>>, TError = AxiosError<unknown>>(
  options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof healthCheck>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getHealthCheckInfiniteQueryOptions(options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getHealthCheckQueryOptions = <TData = Awaited<ReturnType<typeof healthCheck>>, TError = AxiosError<unknown>>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof healthCheck>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getHealthCheckQueryKey();

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof healthCheck>>> = ({ signal }) => healthCheck({ signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof healthCheck>>, TError, TData> & { queryKey: QueryKey }
}

export type HealthCheckQueryResult = NonNullable<Awaited<ReturnType<typeof healthCheck>>>
export type HealthCheckQueryError = AxiosError<unknown>

/**
 * @summary 서비스 상태 확인
 */
export const useHealthCheck = <TData = Awaited<ReturnType<typeof healthCheck>>, TError = AxiosError<unknown>>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof healthCheck>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getHealthCheckQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



